import React, { createElement } from 'react';
import { Link } from 'react-router-dom';
import { Download } from 'lucide-react';
import { Logo } from './Logo';
import { useT } from '../utils/i18n';
import { CredentialsBar } from './CredentialsBar';
// ============================================================
// TEMPORARY — Home-tile PNG download. Mirrors the green tile
// reference but with the colors inverted: white background, deep
// brand green art, edge-to-edge "253" + "GRAVEL". Remove this
// block AND the matching row in the footer JSX once downloaded.
// ============================================================
const HOME_TILE_GREEN = '#006241';
function homeTileSvg(size: number) {
  // textLength + lengthAdjust forces the digits/wordmark into a fixed
  // pixel width regardless of which font the rasterizer falls back to,
  // so "253" never clips when exported to PNG.
  // Sizing pushes the lockup as close to the canvas edges as possible
  // without clipping the cap height / descender of either line.
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" width="${size}" height="${size}">
  <rect width="${size}" height="${size}" fill="#ffffff"/>
  <text x="${size * 0.03}" y="${size * 0.72}" font-family="Inter, Helvetica, Arial, sans-serif" font-weight="900" font-size="${size * 0.78}" textLength="${size * 0.94}" lengthAdjust="spacingAndGlyphs" fill="${HOME_TILE_GREEN}">253</text>
  <text x="${size * 0.05}" y="${size * 0.96}" font-family="Inter, Helvetica, Arial, sans-serif" font-weight="800" font-size="${size * 0.22}" textLength="${size * 0.9}" lengthAdjust="spacingAndGlyphs" fill="${HOME_TILE_GREEN}">GRAVEL</text>
</svg>`;
}
function downloadHomeTilePng() {
  const SIZE = 1024;
  const svg = homeTileSvg(SIZE);
  const svgBlob = new Blob([svg], {
    type: 'image/svg+xml;charset=utf-8'
  });
  const svgUrl = URL.createObjectURL(svgBlob);
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = SIZE;
    canvas.height = SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(img, 0, 0, SIZE, SIZE);
    URL.revokeObjectURL(svgUrl);
    canvas.toBlob(pngBlob => {
      if (!pngBlob) return;
      const pngUrl = URL.createObjectURL(pngBlob);
      const a = document.createElement('a');
      a.href = pngUrl;
      a.download = '253gravel-home-tile.png';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(pngUrl), 1000);
    }, 'image/png');
  };
  img.onerror = () => URL.revokeObjectURL(svgUrl);
  img.src = svgUrl;
}
// ============================================================
export function Footer() {
  const t = useT();
  return <footer className="pt-12 border-t border-white/10 transition-colors duration-300 bg-black" data-id="element-774">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12" data-id="element-775">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8 mb-8 border-b border-white/20 pb-8" data-id="element-776">
          <div className="md:col-span-2" data-id="element-777">
            <Link to="/" className="inline-block mb-4" data-id="element-778">
              <Logo imgClassName="h-10 w-auto" invertOnLight={false} cycleEvery={3} data-id="element-779" />
            </Link>
            <p className="text-sm font-medium leading-relaxed max-w-sm text-[#ffffff]" data-id="element-780">
              {t('footer.tagline')}
            </p>
          </div>

          <div data-id="element-781">
            <h4 className="font-heading text-[#ffffff] font-bold mb-4" data-id="element-782">
              {t('footer.contact')}
            </h4>
            <ul className="space-y-3 text-sm font-medium text-[#ffffff]" data-id="element-783">
              <li data-id="element-784">
                <a href="tel:206-619-3742" className="hover:text-white/70 transition-colors" data-id="element-785">
                  (206) 619-3742
                </a>
              </li>
              <li data-id="element-786">
                <a href="mailto:info@253gravel.com" className="hover:text-white/70 transition-colors" data-id="element-787">
                  info@253gravel.com
                </a>
              </li>
              <li data-id="element-788">Bonney Lake, WA</li>
            </ul>
          </div>

          <div data-id="element-789">
            <h4 className="font-heading text-[#ffffff] font-bold mb-4" data-id="element-790">
              {t('footer.hours')}
            </h4>
            <ul className="space-y-3 text-sm font-medium text-[#ffffff]" data-id="element-791">
              <li className="flex justify-between" data-id="element-792">
                <span data-id="element-793">{t('footer.monFri')}</span>
                <span data-id="element-794">6:00 AM – 5:00 PM</span>
              </li>
              <li className="flex justify-between" data-id="element-795">
                <span data-id="element-796">{t('footer.saturday')}</span>
                <span data-id="element-797">7:00 AM – 3:00 PM</span>
              </li>
              <li className="flex justify-between" data-id="element-798">
                <span data-id="element-799">{t('footer.sunday')}</span>
                <span data-id="element-800">{t('footer.closed')}</span>
              </li>
            </ul>
          </div>

          <div data-id="element-801">
            <h4 className="font-heading text-[#ffffff] font-bold mb-4" data-id="element-802">
              {t('footer.resources')}
            </h4>
            <ul className="space-y-3 text-sm font-medium text-[#ffffff]" data-id="element-803">
              <li data-id="element-804">
                <Link to="/compare" className="hover:text-white/70 transition-colors" data-id="element-805">
                  {t('nav.compareMaterials')}
                </Link>
              </li>
              <li data-id="element-806">
                <Link to="/delivery-prep" className="hover:text-white/70 transition-colors" data-id="element-807">
                  {t('footer.deliveryPrep')}
                </Link>
              </li>
              <li data-id="element-808">
                <Link to="/delivery-zones" className="hover:text-white/70 transition-colors" data-id="element-809">
                  {t('nav.deliveryZones')}
                </Link>
              </li>
              <li data-id="element-810">
                <Link to="/diy-help" className="hover:text-white/70 transition-colors" data-id="element-811">
                  {t('footer.diyGuide')}
                </Link>
              </li>
              <li data-id="element-812">
                <Link to="/help" className="hover:text-white/70 transition-colors" data-id="element-813">
                  {t('footer.materialQuiz')}
                </Link>
              </li>
              <li data-id="element-814">
                <Link to="/service-areas" className="hover:text-white/70 transition-colors" data-id="element-815">
                  Service Areas
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* TEMPORARY — Home-tile PNG download. Remove once client has the file. */}
        <div className="mb-6 -mt-2 rounded-2xl border border-amber-400/30 bg-amber-400/5 p-5" data-id="element-816">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-id="element-817">
            <div className="flex items-center gap-4" data-id="element-818">
              {/* Inline preview swatch */}
              <div className="w-14 h-14 rounded-xl bg-white border border-amber-400/30 flex items-center justify-center shrink-0" data-id="element-819">
                <div className="flex flex-col items-center leading-none" data-id="element-820">
                  <span className="font-extrabold text-[#006241] text-[15px] tracking-tight leading-none" data-id="element-821">
                    253
                  </span>
                  <span className="font-extrabold text-[#006241] text-[6px] tracking-[0.1em] leading-none mt-1" data-id="element-822">
                    GRAVEL
                  </span>
                </div>
              </div>
              <div data-id="element-823">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-300" data-id="element-824">
                  Home Tile — Temporary
                </p>
                <p className="text-sm text-white/70 mt-1" data-id="element-825">
                  Square 1024×1024 PNG · white background, brand green art.
                </p>
              </div>
            </div>
            <button type="button" onClick={downloadHomeTilePng} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white hover:bg-stone-100 text-stone-900 text-sm font-bold transition-colors shrink-0" data-id="element-826">
              <Download className="w-4 h-4" data-id="element-827" />
              Download PNG
            </button>
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center text-sm font-medium text-white/60" data-id="element-828">
          <p data-id="element-829">
            &copy; {new Date().getFullYear()} 253Gravel. {t('footer.rights')}
          </p>
          <div className="mt-4 md:mt-0 space-x-6" data-id="element-830">
            <a href="#" className="hover:text-white transition-colors" data-id="element-831">
              {t('footer.privacy')}
            </a>
            <a href="#" className="hover:text-white transition-colors" data-id="element-832">
              {t('footer.terms')}
            </a>
          </div>
        </div>
      </div>
      <CredentialsBar data-id="element-833" />
    </footer>;
}